import { createColumnHelper } from '@tanstack/react-table';
import { Virtualizer } from '@tanstack/react-virtual';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { HiOutlineInformationCircle, HiOutlineCalendarDays, HiOutlineDocumentText } from 'react-icons/hi2';

import { TherapeuticClassReportItem } from '@/api/use-therapeutic-class-report';
import { DataTable } from '@/components-new/data-table';
import { useDataTable } from '@/components-new/data-table/use-data-table';
import { PdlStatus, pdlStatusToCode } from '@/features/coverage/types/pdl-status';
import { PdlStatusBadge } from '@/features/drugs/components/pdl-status-badge';
import { getSortableCoverageTag } from '@/features/drugs/types/coverage-tag';
import { useMeetingDetails } from '@/features/calendar/api/use-meeting-details';
import { MeetingDetailsDrawer } from '@/features/calendar/components/meetings';
import { Meeting } from '@/features/calendar/types/meeting';

import { PaCriteriaDrawer } from './pa-criteria-drawer';

/**
 * Cell display for the state name in grouped table rows.
 * Enhanced for virtualization compatibility.
 */
const StateCell = ({ state, isGroupParent }: { state: string, isGroupParent: boolean }) => {
  if (isGroupParent) {
    return (
      <span className="font-medium text-zinc-900 dark:text-zinc-100 py-1 block">
        {state}
      </span>
    );
  }
  return (
    <span className="opacity-0 transition-all group-hover/table-row:opacity-100 text-zinc-600 dark:text-zinc-400 py-1 block">
      {state}
    </span>
  );
};

/**
 * Cell display for the drug name with coverage tags.
 * Enhanced for virtualization compatibility.
 */
const DrugNameWithTagsCell = ({ 
  drug, 
  coverageTags 
}: { 
  drug: TherapeuticClassReportItem['drug'];
  coverageTags: TherapeuticClassReportItem['coverageTags'];
}) => {
  const drugName = drug?.label || '';
  
  const tags = coverageTags?.filter(tag => !tag.isDefault)
    .map(tag => ({
      ...tag,
      type: tag.type || { code: 'O', name: 'Other' },
      canDelete: tag.canDelete ?? false
    }))
    .map(tag => getSortableCoverageTag(tag))
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map(sortableTag => sortableTag.coverageTag);

  return (
    <div className="flex flex-row gap-1 py-1">
      <span className="text-zinc-900 dark:text-zinc-100">{drugName}</span>
      {tags && tags.length > 0 && (
        <span className="text-zinc-500 dark:text-zinc-400">
          {tags.map((tag) =>
            // @ts-ignore
            tag.type == 'Indication' ? `for ${tag.name}` : tag.name
          ).join(', ')}
        </span>
      )}
    </div>
  );
};

/**
 * Cell display for dates with optional meeting details button.
 * Enhanced for virtualization compatibility.
 */
const DateWithMeetingCell = ({ 
  dateString, 
  meetingId, 
  meetingType,
  onShowMeetingDetails 
}: { 
  dateString: string;
  meetingId: number | null;
  meetingType: 'last' | 'next';
  onShowMeetingDetails: (meetingId: number, meetingType: 'last' | 'next') => void;
}) => {
  if (!dateString) return '-';
  
  let formattedDate: string;
  try {
    formattedDate = DateTime.fromISO(dateString, { zone: 'utc' })
      .toLocal()
      .toLocaleString(DateTime.DATE_MED);
  } catch {
    formattedDate = dateString;
  }

  return (
    <div className="flex items-center gap-2">
      <span>{formattedDate}</span>
      {meetingId && (
        <button
          onClick={() => onShowMeetingDetails(meetingId, meetingType)}
          className="inline-flex items-center justify-center w-7 h-7 bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 hover:text-blue-800 transition-all duration-200 shadow-sm hover:shadow-md"
          title={`View ${meetingType === 'last' ? 'last review' : 'next review'} meeting details`}
          aria-label={`View ${meetingType === 'last' ? 'last review' : 'next review'} meeting details`}
        >
          <HiOutlineCalendarDays className="size-4" />
        </button>
      )}
    </div>
  );
};

interface WindowedTherapeuticClassTableProps {
  data: TherapeuticClassReportItem[];
  showDrugColumn?: boolean;
  showStepTherapyColumn?: boolean;
  isLoading?: boolean;
  hasNextPage?: boolean;
  fetchNextPage?: () => void;
  isFetchingNextPage?: boolean;
  enableVirtualization?: boolean; // Allow disabling virtualization if needed
  // Sorting props
  sortBy?: string;
  sortDirection?: 'Ascending' | 'Descending';
  onSortChange?: (columnId: string, direction: 'Ascending' | 'Descending') => void;
}

export const WindowedTherapeuticClassTable = ({
  data,
  showDrugColumn = true,
  showStepTherapyColumn = false,
  isLoading = false,
  hasNextPage = false,
  fetchNextPage,
  isFetchingNextPage = false,
  enableVirtualization = true, // Default to enabled, but allow override
  // Sorting props
  sortBy,
  sortDirection = 'Ascending',
  onSortChange,
}: WindowedTherapeuticClassTableProps) => {
  const columnHelper = createColumnHelper<TherapeuticClassReportItem>();
  const [selectedPaCriteria, setSelectedPaCriteria] = useState<TherapeuticClassReportItem | null>(null);
  const [selectedMeetingId, setSelectedMeetingId] = useState<number | null>(null);
  const [selectedMeetingType, setSelectedMeetingType] = useState<'last' | 'next' | null>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);
  const lastFetchTriggerRef = useRef<number>(0); // Track last fetch trigger time
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const rowVirtualizerRef = useRef<Virtualizer<HTMLDivElement, HTMLTableRowElement>>(null!);

  // Fetch meeting details when a meeting is selected
  const { data: selectedMeeting } = useMeetingDetails(selectedMeetingId || undefined, !!selectedMeetingId);

  // Handler to show meeting details
  const showMeetingDetails = useCallback((meetingId: number, meetingType: 'last' | 'next') => {
    setSelectedMeetingId(meetingId);
    setSelectedMeetingType(meetingType);
  }, []);

  // Handler to close meeting details
  const closeMeetingDetails = useCallback(() => {
    setSelectedMeetingId(null);
    setSelectedMeetingType(null);
  }, []);

  // Use IntersectionObserver for better infinite scroll performance (non-virtualized)
  // For virtualized tables, we'll use virtualizer's scroll events instead
  useEffect(() => {
    if (enableVirtualization) return; // Skip for virtualized tables
    if (!loadMoreTriggerRef.current || !hasNextPage || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && fetchNextPage) {
          const now = Date.now();
          if (now - lastFetchTriggerRef.current > 300) {
            lastFetchTriggerRef.current = now;
            fetchNextPage();
          }
        }
      },
      {
        root: tableContainerRef.current,
        rootMargin: '200px',
        threshold: 0.1
      }
    );

    observer.observe(loadMoreTriggerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, enableVirtualization]);

  // Virtualized infinite scroll handling - find the actual scrollable element
  useEffect(() => {
    if (!enableVirtualization) return;
    if (!hasNextPage || isFetchingNextPage || !fetchNextPage) return;

    // With virtualization, the scroll element is inside the table container
    // We need to find the actual scrollable div inside the DataTable
    const findScrollableElement = () => {
      if (!tableContainerRef.current) return null;
      
      // The scrollable element should be a div with overflow-auto inside our container
      const scrollableDiv = tableContainerRef.current.querySelector('div[style*="overflow"]') ||
                           tableContainerRef.current.querySelector('.overflow-auto') ||
                           tableContainerRef.current.querySelector('div'); // fallback to first div
      
      return scrollableDiv as HTMLElement;
    };

    const scrollElement = findScrollableElement();
    if (!scrollElement) {
      return;
    }

    const handleVirtualizedScroll = () => {
      // Clear previous timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      
      // Debounce scroll events
      scrollTimeoutRef.current = setTimeout(() => {
        const { scrollTop, scrollHeight, clientHeight } = scrollElement;
        const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;
        const now = Date.now();
        
        // Trigger when near bottom (85% for virtualized tables)
        if (
          scrollPercentage >= 0.85 && 
          hasNextPage && 
          !isFetchingNextPage && 
          fetchNextPage &&
          now - lastFetchTriggerRef.current > 300
        ) {
          lastFetchTriggerRef.current = now;
          fetchNextPage();
        }
      }, 100);
    };

    scrollElement.addEventListener('scroll', handleVirtualizedScroll, { passive: true });
    
    return () => {
      scrollElement.removeEventListener('scroll', handleVirtualizedScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, enableVirtualization, data.length]); // Include data.length to re-setup after data changes

  // Fallback scroll handler for non-virtualized tables
  useEffect(() => {
    if (enableVirtualization) return; // Skip for virtualized tables
    if (!tableContainerRef.current) return;

    const container = tableContainerRef.current;
    
    const handleScroll = () => {
      // Clear previous timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      
      // Debounce scroll events for better performance
      scrollTimeoutRef.current = setTimeout(() => {
        const { scrollTop, scrollHeight, clientHeight } = container;
        
        // Trigger when near bottom (90% for non-virtualized tables)
        const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;
        const now = Date.now();
        
        if (
          scrollPercentage >= 0.90 && 
          hasNextPage && 
          !isFetchingNextPage && 
          fetchNextPage &&
          now - lastFetchTriggerRef.current > 300
        ) {
          lastFetchTriggerRef.current = now;
          fetchNextPage();
        }
      }, 50);
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, enableVirtualization]);

  // Define table columns
  const columns = useMemo(() => {
    const baseColumns: any[] = [
      columnHelper.accessor(
        (row) => row.state?.label || '',
        {
          id: 'state',
          header: 'State',
          enableSorting: false,
          cell: ({ cell, row }) => {
            // Determine if the row is the first in the group
            const parentRow = row.getParentRow();
            const isFirstRowInGroup = parentRow?.getLeafRows().findIndex(leaf => leaf.id === row.id) === 0;

            return (
              <StateCell state={cell.getValue()} isGroupParent={isFirstRowInGroup}/>
            );
          },
          meta: {
            cellProps: ({ row }) => {
              const parentRow = row.getParentRow();
              const groupCount = parentRow?.getLeafRows().length ?? 0;
              const rowPosition = parentRow?.getLeafRows().findIndex(leaf => leaf.id === row.id);
              const isLastRow = rowPosition === groupCount - 1;

              return {
                bordered: isLastRow,
              };
            }
          },
          filterFn: 'arrIncludesSome',
        }
      ),
    ];

    if (showDrugColumn) {
      baseColumns.push(
        columnHelper.accessor(
          (row) => row.drug?.label || '',
          {
            id: 'drug',
            header: 'Drug',
            enableSorting: false,
            enableGrouping: false,
            cell: ({ row }) => (
              <DrugNameWithTagsCell 
                drug={row.original.drug} 
                coverageTags={row.original.coverageTags || []}
              />
            ),
          }
        )
      );
    }

    // Add remaining columns
    baseColumns.push(
      columnHelper.accessor(
        (row) => row.pdlStatus || '',
        {
          id: 'pdlStatus',
          header: 'PDL Status',
          enableSorting: false,
          enableGrouping: false,
          cell: ({ getValue }) => {
            const status = getValue() || '';
            const pdlStatusValue = pdlStatusToCode(status);
            
            return <PdlStatusBadge pdlStatus={pdlStatusValue}/>;
          },
        }
      )
    );

    // Add step therapy column for internal users
    if (showStepTherapyColumn) {
      baseColumns.push(
        columnHelper.accessor(
          (row) => row.stepTherapyCount || 0,
          {
            id: 'stepTherapy',
            header: 'Step Therapy',
            enableSorting: false,
            enableGrouping: false,
            cell: ({ getValue, row }) => {
              const stepTherapyCount = getValue();
              const hasAdditionalSteps = row.original.hasAdditionalSteps;
              
              if (stepTherapyCount === null || stepTherapyCount === 0) {
                return <span className="text-gray-500">—</span>;
              }
              
              return (
                <span className="font-medium text-gray-900">
                  {stepTherapyCount}{hasAdditionalSteps ? '+' : ''}
                </span>
              );
            },
          }
        )
      );
    }

    baseColumns.push(
      columnHelper.display({
        id: 'paTypes',
        header: 'PA Type',
        cell: ({ row }) => {
          const paTypes = row.original.paTypes || [];
          const stepTherapyCount = row.original.stepTherapyCount;
          const paCriteriaDescription = row.original.paCriteriaDescription;
          const paCriteriaSourceUrl = row.original.paCriteriaSourceUrl;
          
          const getPaTypesString = (paTypes: Array<{ label?: string } | string>) => {
            if (!paTypes || paTypes.length === 0) return '—';
            return paTypes.map(pt => typeof pt === 'string' ? pt : pt.label || pt).join(', ');
          };

          const hasAdditionalInfo = paCriteriaDescription || paCriteriaSourceUrl || stepTherapyCount;
          const hasPaTypes = paTypes && paTypes.length > 0;
          const isClickable = hasAdditionalInfo || hasPaTypes;
          
          return (
            <div className="flex items-center gap-2">
              <span className="text-left">
                {getPaTypesString(paTypes)}
              </span>
              {isClickable && (
                <button
                  onClick={() => setSelectedPaCriteria(row.original)}
                  className="inline-flex items-center justify-center w-7 h-7 bg-amber-100 text-amber-700 rounded-full hover:bg-amber-200 hover:text-amber-800 transition-all duration-200 shadow-sm hover:shadow-md"
                  title="View PA criteria details"
                  aria-label="View PA criteria details"
                >
                  <HiOutlineDocumentText className="size-4" />
                </button>
              )}
            </div>
          );
        },
      }),
      columnHelper.accessor(
        (row) => row.lastReviewDate || '',
        {
          id: 'lastReviewDate',
          header: 'Date of Last Reviewed',
          enableSorting: false,
          enableGrouping: false,
          cell: ({ getValue, row }) => (
            <DateWithMeetingCell
              dateString={getValue()}
              meetingId={row.original.lastReviewMeetingId}
              meetingType="last"
              onShowMeetingDetails={showMeetingDetails}
            />
          ),
        }
      ),
      columnHelper.accessor(
        (row) => row.nextReviewDate || '',
        {
          id: 'nextreviewdate',
          header: 'Date of Next Review',
          enableSorting: true,
          enableGrouping: false,
          cell: ({ getValue, row }) => (
            <DateWithMeetingCell
              dateString={getValue()}
              meetingId={row.original.nextReviewMeetingId}
              meetingType="next"
              onShowMeetingDetails={showMeetingDetails}
            />
          ),
        }
      )
    );

    return baseColumns;
  }, [showDrugColumn, showStepTherapyColumn, columnHelper, showMeetingDetails]);

  // Transform tableData to ensure unique IDs for React keys
  const transformedTableData = useMemo(() => {
    return data.map((row, index: number) => ({
      ...row,
      uniqueId: showDrugColumn && row.state?.id && row.drug?.id 
        ? `${row.state.id}-${row.drug.id}-${index}` 
        : row.state?.id 
          ? `${row.state.id}-${index}` 
          : `row-${index}`,
    }));
  }, [data, showDrugColumn]);

  // Create a stable sorting state that only updates when props change
  const sortingState = useMemo(() => {
    return sortBy ? [{
      id: sortBy,
      desc: sortDirection === 'Descending'
    }] : [];
  }, [sortBy, sortDirection]);

  const table = useDataTable({
    data: transformedTableData,
    columns,
    enableSorting: true,
    enableColumnPinning: true,
    enableStickyHeader: false, // Disable to prevent multiple scroll containers
    enableGrouping: true,
    enableRowSelection: false, // Disable row selection for better performance
    enableMultiRowSelection: false,
    enableSubRowSelection: false,
    enableRowVirtualization: enableVirtualization,
    showAggregateGroupRow: false, // Hide group header rows to prevent empty spaces in virtualization
    layoutMode: 'semantic', // Use semantic layout for better virtualization compatibility
    rowVirtualizerOptions: enableVirtualization ? {
      overscan: 10, // Render extra rows for smoother scrolling
      estimateSize: () => 65, // Estimated row height in pixels
      getItemKey: (index: number) => {
        const row = transformedTableData[index];
        return row?.uniqueId || `row-${index}`;
      },
    } : undefined,
    rowVirtualizerInstanceRef: enableVirtualization ? rowVirtualizerRef : undefined, // Get access to virtualizer instance
    // Use initialState with a stable reference for grouping and pinning
    initialState: {
      grouping: ['state'],
      expanded: true,
      columnPinning: { left: ['state'], right: [] },
    },
    // Use state only for sorting to avoid infinite loops
    state: {
      sorting: sortingState,
    },
    tableBodyRowProps: () => ({
      className: 'group/table-row hover:bg-zinc-50 transition-colors duration-150'
    }),
    getRowId: (row) => (row as any).uniqueId,
    // Performance optimizations
    debugTable: false,
    debugHeaders: false,
    debugColumns: false,
    // Manual sorting since we handle it server-side
    manualSorting: true,
    onSortingChange: (updater) => {
      if (typeof updater === 'function') {
        const currentSorting = sortingState;
        const newSorting = updater(currentSorting);
        
        if (newSorting.length > 0 && onSortChange) {
          const sort = newSorting[0];
          const newDirection = sort.desc ? 'Descending' : 'Ascending';
          onSortChange(sort.id, newDirection);
        } else if (newSorting.length === 0 && onSortChange) {
          // Clear sorting
          onSortChange('', 'Ascending');
        }
      }
    },
  });

  if (isLoading) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-8 text-center">
        <div className="mx-auto size-8 animate-spin rounded-full border-4 border-gray-300 border-t-blue-600"></div>
        <p className="mt-2 text-sm text-gray-500">Loading therapeutic class data...</p>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-8 text-center">
        <p className="text-sm text-gray-500">
          No coverage data available for the selected therapeutic class and drugs.
        </p>
      </div>
    );
  }

  return (
    <>
      <div 
        ref={tableContainerRef}
        className={`max-h-[calc(100vh-200px)] min-h-[600px] scroll-smooth [contain:layout_style_paint] [will-change:scroll-position] border border-zinc-200 rounded-lg bg-white dark:bg-zinc-800 dark:border-zinc-700 ${
          enableVirtualization ? 'overflow-hidden' : 'overflow-auto'
        }`}
        data-virtualization={enableVirtualization ? 'enabled' : 'disabled'}
      >
        <DataTable table={table}/>
      </div>
      
      {/* Infinite scroll trigger element - larger for better detection */}
      {hasNextPage && (
        <div 
          ref={loadMoreTriggerRef}
          className="h-8 w-full flex items-center justify-center"
          aria-hidden="true"
        >
          <div className="text-xs text-gray-400">Loading more...</div>
        </div>
      )}
      
      {/* Loading indicator for infinite scrolling */}
      {isFetchingNextPage && (
        <div className="flex items-center justify-center border-t border-gray-200 py-6">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <div className="size-5 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600"></div>
            Loading more data...
          </div>
        </div>
      )}
      
      {/* End of data indicator */}
      {!hasNextPage && data.length > 0 && !isLoading && (
        <div className="flex items-center justify-center border-t border-gray-200 py-3">
          <div className="text-sm text-gray-500">
            All data loaded • {data.length} total items
          </div>
        </div>
      )}
      
      <PaCriteriaDrawer
        reportItem={selectedPaCriteria || undefined}
        onClose={() => setSelectedPaCriteria(null)}
      />
      
      <MeetingDetailsDrawer
        meeting={selectedMeeting}
        meetingType={selectedMeetingType || undefined}
        onClose={closeMeetingDetails}
      />
    </>
  );
};
